import ErrorComponent from 'Components/ErrorComponent';
import { errorToast, successToast } from 'Components/Toasts';
import { FormikProps, useFormik } from 'formik';
import apiGlobal from 'global/api.global';
import { RootState } from 'index';
import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query';
import { queryClient } from 'react-query/queryClient';
import { useSelector } from 'react-redux';
import { Button, CardFooter, Col, Input, Row, Table, } from 'reactstrap';
import { queryKeyes } from 'shared/queryKeys';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import { Get } from 'VesselMaster/vesselMaster.hooks';
import { VesselConfigrationTabs } from 'shared/constants';
import { commonValidationMessages } from 'Components/ValidationErrorMessages';
import * as Yup from "yup";
import env from 'environment_system/env_system';
import ErrorTooltip from 'Components/ErrorTooltip';
import { compareObjectIgnoreType, errResponse, isConfigurationButtonDisabled } from 'GenericForms/Helper';
import DeletePopOver from 'Components/DeletePopOver';
import ToolTip from 'Components/ToolTip';
import BaseButton from 'Components/BaseButton';
import FormLoading from 'Components/FormLoading';

interface SpeedConsumptionType {
    VesselConfActiveTab: number,
    setCheckValuesBeforeSwitch: (value: boolean) => void;
    setTabName?: (value: string) => void;
    setFormik?: (value: FormikProps<any>) => void;
    deletePopOverActive?: boolean;
    setDeletePopOverActive?: (value: boolean) => void;
    activeSettingsTab: number;
    setVesselDataTabName?: (value: string) => void;
    saveBeforeChangePopUpInsideTab: boolean;
    refreshForm: boolean;
    saveBeforeChangePopUp: boolean;
    setDisableYesButton?: (value: boolean) => void;
}

const SeaTrialSpeedConsumption = ({
    VesselConfActiveTab,
    deletePopOverActive,
    setDeletePopOverActive,
    setCheckValuesBeforeSwitch,
    setTabName,
    setFormik,
    activeSettingsTab,
    setVesselDataTabName,
    saveBeforeChangePopUpInsideTab,
    refreshForm,
    saveBeforeChangePopUp,
    setDisableYesButton
}: SpeedConsumptionType) => {
    /** State variables */
    const { VesselID, Vessels } = useSelector((state: RootState) => state.Reporting);
    const vessel = Vessels.find((rec: any) => rec.id === VesselID);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [deleteBool, setDeleteBool] = useState(false);
    const [deleteId, setDeleteId] = useState<number>(null);
    const [deleteIndex, setDeleteIndex] = useState<number>(null);
    const [deleteString, setDeleteString] = useState<string>(null);
    /** State variables end */

    /** useQueries */
    /** Load speed and consumption on vessel */
    const {
        data: SeaTrial,
        isLoading: SeaTrialLoading,
        isError: SeaTrialError,
    }: { data: any[]; isLoading: any; isError: any } = useQuery(
        [queryKeyes.vessel.OnboardingSeaTrialSpeedConsumptionbyVessel.key, VesselID],
        async () => {
            return await Get(queryKeyes.vessel.OnboardingSeaTrialSpeedConsumptionbyVessel.url(VesselID), VesselID);
        },
        {
            enabled: true,
            staleTime: Infinity,
        }
    );

    const getInitialValues = () => {
        const createDefaultItem = () => ({
            ballast_fc: null as number | null,
            ballast_power: null as number | null,
            ballast_sfoc: null as number | null,
            laden_fc: null as number | null,
            laden_power: null as number | null,
            laden_sfoc: null as number | null,
            speed: null as number | null,
            max_speed: null as number | null,
            min_speed: null as number | null,
            vessel: VesselID,
        });

        return {
            sea_trial_data: SeaTrial && SeaTrial.length > 0 ? SeaTrial : [createDefaultItem()],
        };
    };

    const deleteSeaTrial = async (SeaTrialSpeedConsumptionFormik?: any) => {
        const key = deleteString as 'sea_trial_data' | 'ladenSpeedConsumption' | 'ballastSpeedConsumption';
        const formikArray = SeaTrialSpeedConsumptionFormik?.values?.[key];

        if (!Array.isArray(formikArray)) {
            return;
        }
        if (deleteId) {
            // Server-side delete
            try {
                const res = await apiGlobal.delete(
                    `${queryKeyes.vessel.SeaTtialSpeedConsumption.url()}${deleteId}/`
                );

                if ([200, 204].includes(res.status)) {
                    successToast("Data deleted successfully!");

                    await queryClient.invalidateQueries([
                        queryKeyes.vessel.OnboardingSeaTrialSpeedConsumptionbyVessel.key,
                        VesselID,
                    ]);

                    formikArray.splice(deleteIndex, 1);
                }
            } catch (err: any) {
                if (errResponse.includes(err?.response?.status)) {
                    errorToast("Internal error occurred, please contact the admin");
                }
            }
        } else if (deleteIndex === 0) {
            Object.keys(formikArray[0]).forEach((key) => {
                formikArray[0][key] = ''; // or null
            });
        } else {
            formikArray.splice(deleteIndex, 1);
        }

        SeaTrialSpeedConsumptionFormik.setFieldValue(key, [...formikArray]);

        // Reset state
        setDeleteBool(false);
        setDeleteIndex(null);
        setDeleteId(null);
        setDeleteString(null);
        setRefreshKey(prev => prev + 1);
    };


    /** formik object */
    const SeaTrialSpeedConsumptionFormik: any = useFormik({
        enableReinitialize: true,
        initialValues: getInitialValues(),
        validationSchema: Yup.object().shape({
            sea_trial_data: Yup.array()
                .of(
                    Yup.object().shape({
                        speed: Yup.number()
                            .required(commonValidationMessages.required)
                            .moreThan(0, "Speed must be greater than 0"),
                        ballast_fc: Yup.string()
                            .required(commonValidationMessages.required)
                            .matches(/^\d{0,3}(?:\.\d{1,2})?$/, commonValidationMessages.before3after2),
                        ballast_power: Yup.string()
                            .required(commonValidationMessages.required)
                            .matches(/^\d{0,5}(?:\.\d{1,2})?$/, commonValidationMessages.before5after2),
                        ballast_sfoc: Yup.string()
                            .required(commonValidationMessages.required)
                            .matches(/^\d{0,3}(?:\.\d{1,2})?$/, commonValidationMessages.before3after2),
                        laden_fc: Yup.string()
                            .required(commonValidationMessages.required)
                            .matches(/^\d{0,3}(?:\.\d{1,2})?$/, commonValidationMessages.before3after2),
                        laden_power: Yup.string()
                            .required(commonValidationMessages.required)
                            .matches(/^\d{0,5}(?:\.\d{1,2})?$/, commonValidationMessages.before5after2),
                        laden_sfoc: Yup.string()
                            .required(commonValidationMessages.required)
                            .matches(/^\d{0,3}(?:\.\d{1,2})?$/, commonValidationMessages.before3after2),
                    })
                )
                .test(
                    "unique-speed",
                    "Each speed must be unique",
                    function (value) {
                        if (!Array.isArray(value)) {
                            return true;
                        }
                        const speedMap = new Map();
                        const duplicates: number[] = [];
                        value.forEach((row, index) => {
                            const speed = Number(row?.speed);
                            if (speedMap.has(speed)) {
                                duplicates.push(index, speedMap.get(speed));
                            } else {
                                speedMap.set(speed, index);
                            }
                        });
                        if (duplicates.length > 0) {
                            // Only show one duplicate message per speed
                            const uniqueIndexes = Array.from(new Set(duplicates));
                            return this.createError({
                                path: `sea_trial_data[${uniqueIndexes[0]}].speed`,
                                message: "Speed must be unique",
                            });
                        }

                        return true;
                    }
                )
        }),
        onSubmit: async () => {
            SeaTrialSpeedConsumptionFormik.setSubmitting(true);
            try {
                const res = await apiGlobal.post(
                    `/vessel_speed_sea_trail/`,
                    SeaTrialSpeedConsumptionFormik.values.sea_trial_data
                );
                if (res.status === 200 || res.status === 201) {
                    await queryClient.invalidateQueries(queryKeyes.vessel.OnboardingSeaTrialSpeedConsumptionbyVessel.key);
                    await queryClient.invalidateQueries(queryKeyes.vessel.ConfigurationList.key);
                    setRefreshKey(refreshKey + 1);
                    successToast("Sea trial data saved successfully!");
                }
            } catch (err: any) {
                console.error("Error submitting sea trial data:", err);
                errorToast("Failed to submit sea trial data.");
            }
            SeaTrialSpeedConsumptionFormik.setSubmitting(false);
        }
    })

    const tabName = 'Sea Trial Value Speed & Consumption';

    const handleFormikStateChange = (values: any, initialValues: any) => {
        const areValuesEqual = compareObjectIgnoreType(values, initialValues);
        setTabName(tabName);
        setCheckValuesBeforeSwitch(areValuesEqual);
        setVesselDataTabName(tabName);
        return areValuesEqual;
    };

    //function to calculate  consumption from SFOC
    const calculateFuelConsumption = (sfoc: any, power: any) => {
        if (!sfoc || !power || sfoc <= 0 || power <= 0) {
            return ''
        };

        const fc = (parseFloat(sfoc) * parseFloat(power) * 24) / 1000000;
        return fc.toFixed(0);
    };

    // Function to calculate sfoc from Fuel Consumption  
    // sfoc = FC * 10⁶ / (Power * hr (24))
    const calculatesfoc = (fuelConsumption: any, power: any) => {
        if (!fuelConsumption || !power || fuelConsumption <= 0 || power <= 0) {
            return '';
        }
        const sfoc = (parseFloat(fuelConsumption) * 1000000) / (parseFloat(power) * 24);
        return sfoc.toFixed(2);
    };

    useEffect(() => {
        !deletePopOverActive && setDeleteBool(false)
    }, [deletePopOverActive])

    /**tab switching save */
    useEffect(() => {
        if (
            VesselConfActiveTab === VesselConfigrationTabs.SPEED_CONSUMPTION &&
            !compareObjectIgnoreType(SeaTrialSpeedConsumptionFormik.values, SeaTrialSpeedConsumptionFormik.initialValues)
        ) {
            setFormik(SeaTrialSpeedConsumptionFormik);
            setDisableYesButton(!SeaTrialSpeedConsumptionFormik.isValid);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [VesselConfActiveTab, SeaTrialSpeedConsumptionFormik.values, SeaTrialSpeedConsumptionFormik.isValid]);

    useEffect(() => {
        if (VesselConfActiveTab !== VesselConfigrationTabs.SPEED_CONSUMPTION) {
            SeaTrialSpeedConsumptionFormik?.setTouched({})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [VesselConfActiveTab]);
    useEffect(() => {
        handleFormikStateChange(SeaTrialSpeedConsumptionFormik?.values, SeaTrialSpeedConsumptionFormik?.initialValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [SeaTrialSpeedConsumptionFormik?.values, SeaTrialSpeedConsumptionFormik?.initialValues]);
    /**End of useEffect */

    return (
        <React.Fragment>
            <Row className='mb-3 mt-2'>
                <h5 className='mb-0'>Sea Trial Value Speed & Consumption</h5>
            </Row>
            {(SeaTrialLoading) && <FormLoading />}
            {SeaTrialError && <ErrorComponent message='Unable to load required data!' />}
            {!(SeaTrialLoading) &&
                <form onSubmit={SeaTrialSpeedConsumptionFormik?.handleSubmit} noValidate autoComplete='off' key={`${refreshKey}_${refreshForm}`}>
                    <Row>
                        <Col sm={12}>
                            <React.Fragment>
                                <div className="table-responsive mb-3">
                                    <Table bordered responsive className="mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="p-2 text-center align-middle">#</th>
                                                <th className="p-2 text-center align-middle asteric">Speed</th>
                                                <th className="p-2 text-center align-middle">Condition</th>
                                                <th className="p-2 text-center align-middle asteric">Power</th>
                                                <th className="p-2 text-center align-middle asteric">Fuel Consumption</th>
                                                <th className="p-2 text-center align-middle asteric">SFOC</th>
                                                <th className="p-2 text-center align-middle">Delete</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {SeaTrialSpeedConsumptionFormik?.values?.sea_trial_data?.map((item: any, index: number) => (
                                                <tr key={index}>
                                                    <td className="p-2 text-center align-middle">{index + 1}</td>
                                                    {/* Speed */}
                                                    <td className="p-2 align-middle text-center">
                                                        <div className="input-group">
                                                            <Input
                                                                onFocus={(e) => e.target.select()}
                                                                type="text"
                                                                className="form-control text-right max-width-7"
                                                                id={`speed_${index}_SeaTrial`}
                                                                name={`sea_trial_data[${index}].speed`}
                                                                value={item?.speed ?? ''}
                                                                onChange={SeaTrialSpeedConsumptionFormik.handleChange}
                                                                onBlur={SeaTrialSpeedConsumptionFormik.handleBlur}
                                                                disabled={isConfigurationButtonDisabled(vessel)}
                                                            />
                                                            <div className="input-group-text small-unit-size">knots</div>
                                                        </div>
                                                        {SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.speed &&
                                                            SeaTrialSpeedConsumptionFormik?.touched.sea_trial_data?.[index]?.speed &&
                                                            env?.form_validation === true && activeSettingsTab === 4 && !saveBeforeChangePopUpInsideTab &&
                                                            !saveBeforeChangePopUp &&
                                                            (
                                                                <ErrorTooltip
                                                                    target={`speed_${index}_SeaTrial`}
                                                                    message={SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.speed}
                                                                    open={!!SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.speed}
                                                                />
                                                            )}
                                                    </td>
                                                    <td className="p-2 align-middle text-center">
                                                        <div className="d-flex flex-column gap-2">
                                                            <div className="text-muted">Ballast</div>
                                                            <div className="text-muted mt-3">Laden</div>
                                                        </div>
                                                    </td>
                                                    {/* Power: Ballast + Laden */}
                                                    <td className="p-2 align-middle text-center">
                                                        <div className="d-flex flex-column gap-1">
                                                            <div className="input-group">
                                                                <Input
                                                                    onFocus={(e) => e.target.select()}
                                                                    type="text"
                                                                    className="form-control text-right max-width-7"
                                                                    id={`ballast_power_${index}`}
                                                                    name={`sea_trial_data[${index}].ballast_power`}
                                                                    value={item?.ballast_power ?? ''}
                                                                    onChange={(e) => {
                                                                        SeaTrialSpeedConsumptionFormik.handleChange(e);
                                                                        const power = e.target.value;
                                                                        const sfoc = item?.ballast_sfoc;
                                                                        const fc = item?.ballast_fc;
                                                                        SeaTrialSpeedConsumptionFormik.setFieldValue(
                                                                            `sea_trial_data[${index}].ballast_fc`,
                                                                            calculateFuelConsumption(sfoc, power),
                                                                        );
                                                                        SeaTrialSpeedConsumptionFormik.setFieldValue(
                                                                            `sea_trial_data[${index}].ballast_sfoc`,
                                                                            calculatesfoc(fc, power)
                                                                        );
                                                                    }}
                                                                    onBlur={SeaTrialSpeedConsumptionFormik.handleBlur}
                                                                    disabled={isConfigurationButtonDisabled(vessel)}
                                                                />
                                                                <div className="input-group-text small-unit-size">kW</div>
                                                            </div>
                                                            {SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.ballast_power &&
                                                                SeaTrialSpeedConsumptionFormik?.touched.sea_trial_data?.[index]?.ballast_power &&
                                                                env?.form_validation === true && activeSettingsTab === 4 && !saveBeforeChangePopUpInsideTab &&
                                                                !saveBeforeChangePopUp && (
                                                                    <ErrorTooltip
                                                                        target={`ballast_power_${index}`}
                                                                        message={SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.ballast_power}
                                                                        open={SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.ballast_power}
                                                                    />
                                                                )}
                                                            <div className="input-group">
                                                                <Input
                                                                    onFocus={(e) => e.target.select()}
                                                                    type="text"
                                                                    className="form-control text-right max-width-7"
                                                                    id={`laden_power_${index}`}
                                                                    name={`sea_trial_data[${index}].laden_power`}
                                                                    value={item?.laden_power ?? ''}
                                                                    onChange={(e) => {
                                                                        SeaTrialSpeedConsumptionFormik.handleChange(e);
                                                                        const power = e.target.value;
                                                                        const sfoc = item?.laden_sfoc;
                                                                        const fc = item?.laden_fc;
                                                                        SeaTrialSpeedConsumptionFormik.setFieldValue(
                                                                            `sea_trial_data[${index}].laden_fc`,
                                                                            calculateFuelConsumption(sfoc, power)
                                                                        );
                                                                        SeaTrialSpeedConsumptionFormik.setFieldValue(
                                                                            `sea_trial_data[${index}].laden_sfoc`,
                                                                            calculatesfoc(fc, power)
                                                                        );
                                                                    }}
                                                                    onBlur={SeaTrialSpeedConsumptionFormik.handleBlur}
                                                                    disabled={isConfigurationButtonDisabled(vessel)}
                                                                />
                                                                <div className="input-group-text small-unit-size">kW</div>
                                                            </div>
                                                            {SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.laden_power &&
                                                                SeaTrialSpeedConsumptionFormik?.touched.sea_trial_data?.[index]?.laden_power &&
                                                                env?.form_validation === true && activeSettingsTab === 4 && !saveBeforeChangePopUpInsideTab &&
                                                                !saveBeforeChangePopUp && (
                                                                    <ErrorTooltip
                                                                        target={`laden_power_${index}`}
                                                                        message={SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.laden_power}
                                                                        open={!!SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.laden_power}
                                                                    />
                                                                )}
                                                        </div>
                                                    </td>
                                                    {/* Fuel Consumption */}
                                                    <td className="p-2 align-middle text-center">
                                                        <div className="d-flex flex-column gap-1">
                                                            <div className="input-group">
                                                                <Input
                                                                    onFocus={(e) => e.target.select()}
                                                                    type="text"
                                                                    className="form-control text-right max-width-7"
                                                                    id={`ballast_fc_${index}`}
                                                                    name={`sea_trial_data[${index}].ballast_fc`}
                                                                    value={item?.ballast_fc ?? ''}
                                                                    onChange={(e) => {
                                                                        SeaTrialSpeedConsumptionFormik.handleChange(e);
                                                                        const fc = e.target.value;
                                                                        const power = item?.ballast_power;
                                                                        SeaTrialSpeedConsumptionFormik.setFieldValue(
                                                                            `sea_trial_data[${index}].ballast_sfoc`,
                                                                            calculatesfoc(fc, power)
                                                                        );
                                                                    }}
                                                                    onBlur={SeaTrialSpeedConsumptionFormik.handleBlur}
                                                                    disabled={isConfigurationButtonDisabled(vessel)}
                                                                />
                                                                <div className="input-group-text small-unit-size">mt/day</div>
                                                            </div>
                                                            {SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.ballast_fc &&
                                                                SeaTrialSpeedConsumptionFormik?.touched.sea_trial_data?.[index]?.ballast_fc &&
                                                                env?.form_validation === true && activeSettingsTab === 4 && !saveBeforeChangePopUpInsideTab &&
                                                                !saveBeforeChangePopUp && (
                                                                    <ErrorTooltip
                                                                        target={`ballast_fc_${index}`}
                                                                        message={SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.ballast_fc}
                                                                        open={!!SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.ballast_fc}
                                                                    />
                                                                )}
                                                            <div className="input-group">
                                                                <Input
                                                                    onFocus={(e) => e.target.select()}
                                                                    type="text"
                                                                    className="form-control text-right max-width-7"
                                                                    id={`laden_fc_${index}`}
                                                                    name={`sea_trial_data[${index}].laden_fc`}
                                                                    value={item?.laden_fc ?? ''}
                                                                    onChange={(e) => {
                                                                        SeaTrialSpeedConsumptionFormik.handleChange(e);
                                                                        const fc = e.target.value;
                                                                        const power = item?.laden_power;
                                                                        SeaTrialSpeedConsumptionFormik.setFieldValue(
                                                                            `sea_trial_data[${index}].laden_sfoc`,
                                                                            calculatesfoc(fc, power)
                                                                        );
                                                                    }}
                                                                    onBlur={SeaTrialSpeedConsumptionFormik.handleBlur}
                                                                    disabled={isConfigurationButtonDisabled(vessel)}
                                                                />
                                                                <div className="input-group-text small-unit-size">mt/day</div>
                                                            </div>
                                                            {SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.laden_fc &&
                                                                SeaTrialSpeedConsumptionFormik?.touched.sea_trial_data?.[index]?.laden_fc &&
                                                                env?.form_validation === true && activeSettingsTab === 4 && !saveBeforeChangePopUpInsideTab &&
                                                                !saveBeforeChangePopUp && (
                                                                    <ErrorTooltip
                                                                        target={`laden_fc_${index}`}
                                                                        message={SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.laden_fc}
                                                                        open={!!SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.laden_fc}
                                                                    />
                                                                )}
                                                        </div>
                                                    </td>
                                                    {/* SFOC */}
                                                    <td className="p-2 align-middle text-center">
                                                        <div className="d-flex flex-column gap-1">
                                                            <div className="input-group">
                                                                <Input
                                                                    onFocus={(e) => e.target.select()}
                                                                    type="text"
                                                                    className="form-control text-right max-width-7"
                                                                    id={`ballast_sfoc_${index}`}
                                                                    name={`sea_trial_data[${index}].ballast_sfoc`}
                                                                    value={item?.ballast_sfoc ?? ''}
                                                                    onChange={(e) => {
                                                                        SeaTrialSpeedConsumptionFormik.handleChange(e);
                                                                        const sfoc = e.target.value;
                                                                        const power = item?.ballast_power;
                                                                        SeaTrialSpeedConsumptionFormik.setFieldValue(
                                                                            `sea_trial_data[${index}].ballast_fc`,
                                                                            calculateFuelConsumption(sfoc, power)
                                                                        );
                                                                    }}
                                                                    onBlur={SeaTrialSpeedConsumptionFormik.handleBlur}
                                                                    disabled={isConfigurationButtonDisabled(vessel)}
                                                                />
                                                                <div className="input-group-text small-unit-size">g/kWh</div>
                                                            </div>
                                                            {SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.ballast_sfoc &&
                                                                SeaTrialSpeedConsumptionFormik?.touched.sea_trial_data?.[index]?.ballast_sfoc &&
                                                                env?.form_validation === true && activeSettingsTab === 4 && !saveBeforeChangePopUpInsideTab &&
                                                                !saveBeforeChangePopUp && (
                                                                    <ErrorTooltip
                                                                        target={`ballast_sfoc_${index}`}
                                                                        message={SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.ballast_sfoc}
                                                                        open={!!SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.ballast_sfoc}
                                                                    />
                                                                )}
                                                            <div className="input-group">
                                                                <Input
                                                                    onFocus={(e) => e.target.select()}
                                                                    type="text"
                                                                    className="form-control text-right max-width-7"
                                                                    id={`laden_sfoc_${index}`}
                                                                    name={`sea_trial_data[${index}].laden_sfoc`}
                                                                    value={item?.laden_sfoc ?? ''}
                                                                    onChange={(e) => {
                                                                        SeaTrialSpeedConsumptionFormik.handleChange(e);
                                                                        const sfoc = e.target.value;
                                                                        const power = item?.laden_power;
                                                                        SeaTrialSpeedConsumptionFormik.setFieldValue(
                                                                            `sea_trial_data[${index}].laden_fc`,
                                                                            calculateFuelConsumption(sfoc, power)
                                                                        );
                                                                    }}
                                                                    onBlur={SeaTrialSpeedConsumptionFormik.handleBlur}
                                                                    disabled={isConfigurationButtonDisabled(vessel)}
                                                                />
                                                                <div className="input-group-text small-unit-size">g/kWh</div>
                                                            </div>
                                                            {SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.laden_sfoc &&
                                                                SeaTrialSpeedConsumptionFormik?.touched.sea_trial_data?.[index]?.laden_sfoc &&
                                                                env?.form_validation === true && activeSettingsTab === 4 && !saveBeforeChangePopUpInsideTab &&
                                                                !saveBeforeChangePopUp && (
                                                                    <ErrorTooltip
                                                                        target={`laden_sfoc_${index}`}
                                                                        message={SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.laden_sfoc}
                                                                        open={!!SeaTrialSpeedConsumptionFormik?.errors.sea_trial_data?.[index]?.laden_sfoc}
                                                                    />
                                                                )}
                                                        </div>
                                                    </td>
                                                    {/* Delete Icon */}
                                                    <td className="p-2 text-center align-middle">
                                                        <i
                                                            className={`dripicons-trash icon_s18 ${isConfigurationButtonDisabled(vessel) ? 'disabled-icon' : 'pointer'}`}
                                                            id={`delete_icon_${index}`}
                                                            onClick={() => {
                                                                if (!isConfigurationButtonDisabled(vessel)) {
                                                                    setDeleteBool(true);
                                                                    setDeletePopOverActive(true);
                                                                    setDeleteIndex(index);
                                                                    setDeleteId(item.id ?? null);
                                                                    setDeleteString('sea_trial_data');
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                            {/* Add button row */}
                                            {SeaTrialSpeedConsumptionFormik.values.sea_trial_data.length <= 9 && (
                                                <tr>
                                                    <td colSpan={6} className="p-2 text-center">
                                                        <BaseButton
                                                            title="Add"
                                                            className="pointer"
                                                            variant="primary"
                                                            id={`add_SeaTrial`}
                                                            onClick={() => {
                                                                SeaTrialSpeedConsumptionFormik.setFieldValue('sea_trial_data', [
                                                                    ...SeaTrialSpeedConsumptionFormik.values.sea_trial_data,
                                                                    {
                                                                        ballast_fc: null,
                                                                        ballast_power: null,
                                                                        ballast_sfoc: null,
                                                                        laden_fc: null,
                                                                        laden_power: null,
                                                                        laden_sfoc: null,
                                                                        speed: null,
                                                                        max_speed: null,
                                                                        min_speed: null,
                                                                        vessel: VesselID,
                                                                    },
                                                                ]);
                                                            }}
                                                            icon={<i className="mdi mdi-plus" />}
                                                            iconPosition="left"
                                                        />
                                                        <ToolTip target={`add_SeaTrial`} message="Add speed consumption entry" />
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </React.Fragment>
                        </Col>
                    </Row>
                    {/* Delete PopOver (duplicate for safety) */}
                    {deleteBool && document.getElementById(`delete_icon_${deleteIndex}`) &&
                        <DeletePopOver
                            target={`delete_icon_${deleteIndex}`}
                            state={deleteBool}
                            setState={setDeleteBool}
                            onClick={() => {
                                deleteSeaTrial(SeaTrialSpeedConsumptionFormik)
                            }}
                        />
                    }
                    <CardFooter className='p-2'>
                        <Row className='mb-2'>
                            <Col className='text-end'>
                                <Button
                                    type="submit"
                                    className="btn btn-primary ms-2"
                                    color="primary"
                                    disabled={isConfigurationButtonDisabled(vessel) || SeaTrialSpeedConsumptionFormik?.isSubmitting}
                                >
                                    {SeaTrialSpeedConsumptionFormik?.isSubmitting && <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />}
                                    Save
                                </Button>
                            </Col>
                        </Row>
                        <p className='mb-0'>- This table is necessary for generating alerts in vessel reporting, unless sufficient historical data is available.</p>
                        <FormValuesDebug
                            values={[
                                SeaTrialSpeedConsumptionFormik?.values,
                                SeaTrialSpeedConsumptionFormik?.errors,
                            ]}
                        />
                    </CardFooter>
                </form>
            }
        </React.Fragment >
    )
}

export default SeaTrialSpeedConsumption;
